import React, { createRef, Component } from 'react';
import PropTypes from 'prop-types';

import { ACCORDION_ITEM_DEFAULTS, ACCORDION_ITEM_TYPES } from '../constants';
import Controls from './Controls';
import Panel from './Panel';

export default class Item extends Component {
  static identifier = 'accordion-item';
  static propTypes = ACCORDION_ITEM_TYPES;
  static defaultProps = ACCORDION_ITEM_DEFAULTS;

  controls = createRef();
  panel = createRef();

  componentDidMount() {
    this.props.accordion.registerItem({
      controls: this.controls.current,
      id: this.props.id,
      panel: this.panel.current,
    });
  }

  componentWillUnmount() {
    this.props.accordion.unregisterItem(this.props.id);
  }

  componentDidUpdate(prevProps) {
    const { accordion, id, autoScroll, height } = this.props;
    const { expanded } = accordion;
    const isExpanded = !!expanded[id];
    if (Object.keys(expanded).length === 1 && isExpanded && autoScroll) {
      // only one item is expanded, focus that item
      accordion.focusItem(id, height);
    }
  }

  // Each Context Consumer will re-render on every change.
  // When only allowing one item to be open at a time this is fine but when opening and closing multiple
  // accordions these re-renders can get expensive. This provides an opt-in way to prevent these updates
  shouldComponentUpdate(props, state) {
    const { accordion, allowMultiple, id, optimizeRender } = props;
    const expandedChanged =
      !!accordion.expanded[id] !== !!this.props.accordion.expanded[id];
    if (optimizeRender && !expandedChanged) {
      return false;
    }
    return true;
  }

  toggle = () => {
    if (!this.props.disabled) {
      this.props.accordion.toggle(this.props.id);

      // Allow Item's to provide their own onToggle to be called on a specific item toggle
      // to override the accordion wide on-toggle
      if (this.props.onToggle) {
        this.props.onToggle(this.props.id);
      }
    }
  };

  onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Space') {
      e.preventDefault();
      return this.toggle();
    }

    this.props.accordion.handleKeyDown(e, this.props.id);

    if (this.props.onKeyDown) {
      this.props.onKeyDown(e, this.props.id);
    }
  };

  render() {
    const {
      accordion,
      children,
      content,
      className,
      disabled,
      extraAction,
      id,
      label,
      renderOnExpand,
      removeAriaLabelledBy,
    } = this.props;

    const { expanded } = accordion;
    const isExpanded = !!expanded[id];
    const renderedContent = content || children;

    // If className is passed as object we will take it, otherwise if a user only passes
    // a string for className it will get applied to control by default

    const classes =
      typeof className === 'object' ? className : { control: className };
    const itemClasses = `c-accordion__item ${classes.item}`;
    return (
      <div className={itemClasses} role="presentation">
        <Controls
          actionClassName={classes.action}
          controlClassName={classes.control}
          disabled={disabled}
          expanded={isExpanded}
          extraAction={extraAction}
          id={id}
          onKeyDown={this.onKeyDown}
          toggle={this.toggle}
          triggerClassName={classes.trigger}
          triggerRef={this.controls}
          buttonClassName={classes.button}
        >
          {label}
        </Controls>
        <Panel
          expanded={isExpanded}
          id={id}
          panelClassName={classes.panel}
          panelContentClassName={classes.content}
          panelRef={this.panel}
          renderOnExpand={renderOnExpand}
          removeAriaLabelledBy={removeAriaLabelledBy}
        >
          {renderedContent}
        </Panel>
      </div>
    );
  }
}
